import heic2any from 'heic2any'
import { ImageProps } from 'types/common'

export type BlobToFileProps = {
  dataUrlString: string
  fileName: string
  callback: (file: File) => void
}

export const dataUriToFile = ({
  dataUrlString,
  fileName,
  callback,
}: BlobToFileProps) => {
  return fetch(dataUrlString)
    .then((res) => res.blob())
    .then((blob) => {
      const mimeString = dataUrlString.split(',')[0].split(':')[1].split(';')[0]
      callback(new File([blob], fileName, { type: mimeString }))
    })
}

export const fileToBlob = (file: File) => {
  return new Blob([file], { type: file.type })
}

export const heicToJpg: (
  heic: File,
  callback: (jpeg: File) => void
) => File | void = (heic, callback) => {
  heic2any({
    blob: fileToBlob(heic),
    toType: 'image/jpeg',
  })
    .then((jpegBlob: Blob | Blob[]) => {
      if (jpegBlob instanceof Blob) {
        callback(
          new File([jpegBlob], toJpegExt(heic.name), { type: 'image/jpeg' })
        )
      }
      if (jpegBlob instanceof Array) {
        callback(
          new File(jpegBlob, toJpegExt(heic.name), { type: 'image/jpeg' })
        )
      }
    })
    .catch((e) => {
      console.log(e)
    })
}

export const fileToDataUrl: (
  file: File
) => Promise<string | undefined> = async (file) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader()
    fileReader.onload = () => {
      const data = fileReader.result
      if (typeof data === 'string') {
        resolve(data)
      }
    }
    fileReader.readAsDataURL(file)
  })
}

/**
 * NOTE: 文字列がDataURlかどうか
 * @param str
 */
export const isDataUri: (str: string) => boolean = (str) => {
  // NOTE: https://gist.github.com/khanzadimahdi/bab8a3416bdb764b9eda5b38b35735b8
  const regex = /^data:((?:\w+\/(?:(?!;).)+)?)((?:;[\w\W]*?[^;])*),(.+)$/
  return !!str.match(regex)
}

/**
 * 拡張子を強制的にjpgに変換
 * @param name
 */
export const toJpegExt: (name: string) => string = (name) => {
  return `${name.replace(/\.[^/.]+$/, '')}.jpg`
}

/**
 * fileがheicかどうか windowsでheicのmimeTypeが取得できていない問題の対応
 * @param file
 */
export const isHeic: (file: File) => boolean = (file) => {
  if (file.type === 'image/heic') return true
  const ext = file.name.split('.').pop()
  return !!(ext && ['heic', 'HEIC'].includes(ext))
}

/**
 * ファイルの拡張子とMIMEタイプが一致するかチェック
 * @param file
 */
export const isValidFileType: (file: File) => Promise<boolean> = async (
  file
) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader()
    fileReader.onload = () => {
      const arr = new Uint8Array(fileReader.result as ArrayBuffer).subarray(
        0,
        12
      ) // ファイルタイプをより正確に検出するために12バイトに
      let header = ''
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16).padStart(2, '0')
      }

      const ext = file.name.split('.').pop()?.toLowerCase()
      const mimeType = file.type.toLowerCase()

      let isValid = false

      // PNG: 89 50 4E 47
      if (header.startsWith('89504e47')) {
        isValid = ext === 'png' && mimeType === 'image/png'
      }
      // JPEG: FF D8 FF
      else if (header.startsWith('ffd8ff')) {
        isValid = (ext === 'jpg' || ext === 'jpeg') && mimeType === 'image/jpeg'
      }
      // PDF: 25 50 44 46
      else if (header.startsWith('25504446')) {
        isValid = ext === 'pdf' && mimeType === 'application/pdf'
      }
      // WEBP: 52 49 46 46 (RIFF) の後にファイルサイズとWEBP署名が続く
      else if (
        header.startsWith('52494646') &&
        header.substring(16, 24) === '57454250'
      ) {
        isValid = ext === 'webp' && mimeType === 'image/webp'
      }
      // HEIC/HEIF: HEICファイルに共通するftypヘッダーを確認
      // HEICファイルは通常、4-8バイト目に'ftyp'があり、その後に'heic'や'mif1'などのブランドが続く
      else if (header.substring(8, 16).includes('66747970')) {
        const heicBrands = ['68656963', '6d696631', '6d736631', '68656978'] // 16進数での'heic', 'mif1', 'msf1', 'heix'
        const hasBrand = heicBrands.some((brand) => header.includes(brand))
        isValid =
          (ext === 'heic' || ext === 'heif') &&
          (mimeType === 'image/heic' || mimeType === 'image/heif') &&
          hasBrand
      }
      // ヘッダーで検証できない場合（一部のHEICファイルは検出が難しい場合がある）
      // HEIC/HEIFの拡張子とMIMEタイプのチェックにフォールバック
      else if (
        (ext === 'heic' || ext === 'heif') &&
        (mimeType === 'image/heic' || mimeType === 'image/heif')
      ) {
        isValid = true
      }

      resolve(isValid)
    }
    fileReader.readAsArrayBuffer(file)
  })
}

/**
 * ウイルス感染したファイルがあるかどうか
 * @param images
 */
export const hasInfectedFiles: (images?: ImageProps[]) => boolean = (images) =>
  images
    ? images.some((image) => image.virus_scan_result === 'infected')
    : false
